export enum PoolSizeAdjustmentReason {
  DATA_ENTRY_CORRECTION = 'data-entry-correction',
  FORMAL_ADJUSTMENT = 'formal-adjustment',
}

export enum PoolSizeAdjustmentDirection {
  DECREASE = 'decrease',
  INCREASE = 'increase',
}

export enum PoolSizeAdjustmentIssue {
  BELOW_GRANTED = 'below-granted',
  EMPTY_POOL = 'empty-pool',
  INVALID_AMOUNT = 'invalid-amount',
  TOO_LARGE = 'too-large',
  UNCHANGED = 'unchanged',
}

/**
 * Largest pool size: past it a share count is no longer exact as a JavaScript number, which both the
 * modal and the API compute with. Pool creation is capped at the same value.
 */
export const MAX_POOL_SIZE = Number.MAX_SAFE_INTEGER;

/**
 * A formal adjustment moves the pool by `sharesCount` in `direction` and is recorded in the Events
 * Ledger; a data entry correction replaces the pool size with the corrected total and is not.
 */
export type PoolSizeAdjustmentInput =
  | {
      dilutedSharesCount: number;
      reason: PoolSizeAdjustmentReason.DATA_ENTRY_CORRECTION;
    }
  | {
      direction: PoolSizeAdjustmentDirection;
      reason: PoolSizeAdjustmentReason.FORMAL_ADJUSTMENT;
      sharesCount: number;
    };

export interface PoolSizeAdjustmentResolution {
  /** Signed change applied to the pool size: positive grows it, negative shrinks it. */
  delta: number;
  issue: null | PoolSizeAdjustmentIssue;
  newPoolSize: number;
}

/** Response of `POST companies/:companyId/pools/:poolId/size-adjustments`. */
export interface PoolSizeAdjustmentResult {
  dilutedSharesCount: number;
  /** Events Ledger entry of a formal adjustment; null for a data entry correction. */
  eventId: null | string;
  poolId: string;
}

/**
 * Resolves the pool size an adjustment produces and the first rule it breaks. Kept dependency-free
 * so the backend workflow and the Adjust Pool Size modal apply exactly the same rules: the entered
 * amount must be a positive whole number, and the new size can neither exceed `MAX_POOL_SIZE`, drop
 * below the shares already granted from the pool, nor reach zero. A malformed amount or a size past
 * the ceiling resolves to no change, so there is no new size to preview.
 */
export const resolvePoolSizeAdjustment = ({
  currentPoolSize,
  grantedShares,
  input,
}: {
  currentPoolSize: number;
  grantedShares: number;
  input: PoolSizeAdjustmentInput;
}): PoolSizeAdjustmentResolution => {
  const amount =
    input.reason === PoolSizeAdjustmentReason.FORMAL_ADJUSTMENT
      ? input.sharesCount
      : input.dilutedSharesCount;

  // Any whole number passes here, however large: one past the ceiling is too large, not malformed.
  if (!Number.isInteger(amount) || amount <= 0) {
    return {
      delta: 0,
      issue: PoolSizeAdjustmentIssue.INVALID_AMOUNT,
      newPoolSize: currentPoolSize,
    };
  }

  const newPoolSize =
    input.reason === PoolSizeAdjustmentReason.DATA_ENTRY_CORRECTION
      ? amount
      : input.direction === PoolSizeAdjustmentDirection.INCREASE
        ? currentPoolSize + amount
        : currentPoolSize - amount;

  // An oversized decrease falls through to the floors below, which are what it actually breaks.
  if (newPoolSize > MAX_POOL_SIZE) {
    return {
      delta: 0,
      issue: PoolSizeAdjustmentIssue.TOO_LARGE,
      newPoolSize: currentPoolSize,
    };
  }

  const delta = newPoolSize - currentPoolSize;

  // Report the floor that binds: the granted shares when there are any, otherwise an empty pool
  // (a pool with nothing granted would read "lower than the 0 shares already granted").
  const issue =
    grantedShares > 0 && newPoolSize < grantedShares
      ? PoolSizeAdjustmentIssue.BELOW_GRANTED
      : newPoolSize <= 0
        ? PoolSizeAdjustmentIssue.EMPTY_POOL
        : delta === 0
          ? PoolSizeAdjustmentIssue.UNCHANGED
          : null;

  return { delta, issue, newPoolSize };
};
